import { Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { TriggerbeeApi } from './triggerbee-api.enum';
import { Environment, ENVIRONMENT_DI } from '../../environment/environment';
import { Logger, LoggerFactory } from '../logger.service';
import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEvent } from '@angular/common/http';
import { TargetSite } from '../target-site';
import { CurrentSiteIdStore } from '../current-site-id.store';

const DEFAULT_REQUEST_TIMEOUT_MS = 60_000;
const UPLOAD_DOWNLOAD_TIMEOUT_MS = 5 * 60_000;

@Injectable()
export class HttpRequestDecorator {
  private readonly logger: Logger;

  constructor(
    loggerFactory: LoggerFactory,
    private http: HttpClient,
    @Inject(ENVIRONMENT_DI) private readonly environment: Environment,
    private readonly currentSiteIdStore: CurrentSiteIdStore,
  ) {
    this.logger = loggerFactory.get('HttpRequestDecorator');
  }

  get(url, params?): Observable<any> {
    return this.http.get<any>(url, this.getOptions(params)).pipe(timeout(DEFAULT_REQUEST_TIMEOUT_MS));
  }

  getDownload(url, params?): Observable<any> {
    return this.http
      .get(url, {
        params: params,
        headers: this.getHeaders(),
        responseType: 'text',
        withCredentials: true,
      })
      .pipe(timeout(UPLOAD_DOWNLOAD_TIMEOUT_MS));
  }

  post(url, data, params?): Observable<any> {
    return this.http.post(url, data, this.getOptions(params)).pipe(timeout(DEFAULT_REQUEST_TIMEOUT_MS));
  }

  put(url, data?, params?): Observable<any> {
    return this.http.put(url, data, this.getOptions(params)).pipe(timeout(DEFAULT_REQUEST_TIMEOUT_MS));
  }

  patch(url, data?, params?): Observable<any> {
    const options = this.getOptions(params);
    options.headers = options.headers.set('Content-Type', 'application/json-patch+json');
    return this.http.patch(url, data, options).pipe(timeout(DEFAULT_REQUEST_TIMEOUT_MS));
  }

  delete(url, params?, body?): Observable<any> {
    const options = this.getOptions(params);
    options.body = body;
    return this.http.delete(url, options).pipe(timeout(DEFAULT_REQUEST_TIMEOUT_MS));
  }

  postDownload(url, data, params?): Observable<any> {
    return this.http
      .post(url, data, {
        params: params,
        headers: this.getHeaders(),
        responseType: 'text',
        withCredentials: true,
      })
      .pipe(timeout(UPLOAD_DOWNLOAD_TIMEOUT_MS));
  }

  // responseType blob keeps a large CSV out of an intermediate JS string; the timeout is
  // caller-configurable because exports can stream for minutes.
  postBlobDownloadEvents(url, data, timeoutMs: number = UPLOAD_DOWNLOAD_TIMEOUT_MS): Observable<HttpEvent<Blob>> {
    return this.http
      .post(url, data, {
        headers: this.getHeaders(),
        observe: 'events',
        reportProgress: true,
        responseType: 'blob',
        withCredentials: true,
      })
      .pipe(timeout(timeoutMs));
  }

  getOptions(params?): any {
    return {
      params: params,
      headers: this.getHeaders(),
      withCredentials: true,
    };
  }

  upload(url, formData: FormData, params?): Observable<any> {
    return this.http
      .post(url, formData, {
        params: params,
        headers: this.getMultipartHeaders(),
        withCredentials: true,
      })
      .pipe(timeout(UPLOAD_DOWNLOAD_TIMEOUT_MS));
  }

  uploadPut(url, formData: FormData, params?): Observable<any> {
    return this.http
      .put(url, formData, {
        params: params,
        headers: this.getMultipartHeaders(),
        withCredentials: true,
      })
      .pipe(timeout(UPLOAD_DOWNLOAD_TIMEOUT_MS));
  }

  getHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');

    const siteId = TargetSite.get() ?? this.currentSiteIdStore.get();
    if (siteId) {
      headers = headers.set('X-Target-Site-Id', siteId.toString());
    }

    return headers;
  }

  getMultipartHeaders(): HttpHeaders {
    let headers = new HttpHeaders();

    const siteId = TargetSite.get() ?? this.currentSiteIdStore.get();
    if (siteId) {
      headers = headers.set('X-Target-Site-Id', siteId.toString());
    }

    return headers;
  }

  public getBaseUrl(api: TriggerbeeApi): string {
    switch (api) {
      case TriggerbeeApi.Gateway:
        return this.environment.apiBaseUrls.gateway;
      default:
        this.logger.error(`Unknown api : ${api}`);
    }
    return '';
  }
}
